import { useMutation } from "@apollo/client";

import client from "../../apollo/_client";

import { errorsHandler } from "#/src/transformers";
import { GET_INVENTORY_PART, InventoryPart } from "~/api";
import {
  BULK_DELETE_OWN_OEM_INVENTORY_PARTS_MUTATION,
  CREATE_OWN_OEM_INVENTORY_PART_MUTATION,
  DELETE_OWN_OEM_INVENTORY_PART_MUTATION,
  UPDATE_OWN_OEM_INVENTORY_PART_MUTATION,
  ADD_STOCK_TO_PART_MUTATION,
  UPDATE_STOCK_OPERATION_MUTATION,
  REMOVE_STOCK_OPERATION_MUTATION,
  ALLOCATE_PENDING_STOCK_MUTATION,
} from "~/api/_mutations";

export const useAddPart = () => {
  const [createOwnOemInventoryPart, { error = {}, loading }] = useMutation(
    CREATE_OWN_OEM_INVENTORY_PART_MUTATION,
  );
  return {
    create: (input) =>
      new Promise((resolve, reject) => {
        return createOwnOemInventoryPart({
          variables: {
            input,
          },
          update(cache, { data: { createOwnOemInventoryPart: ref } }) {
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "listOwnOemInventoryPart",
            });
            cache.gc();
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
    loading,
    error,
  };
};

export const useUpdateParts = () => {
  const [updatePart, { error, loading }] = useMutation(
    UPDATE_OWN_OEM_INVENTORY_PART_MUTATION,
  );
  return {
    update: (input, { skipCacheUpdate = false } = {}) =>
      new Promise((resolve, reject) => {
        updatePart({
          variables: {
            input,
          },
          update(cache, { data: { updateOwnOemInventoryPart: ref } }) {
            if (skipCacheUpdate) {
              return;
            }
            cache.modify({
              id: cache.identify({ __typename: "InventoryPart", id: ref._id }),
              fields: {
                listOwnOemInventoryPart(existingRefs = []) {
                  const newRef = cache.writeFragment({
                    data: ref,
                    fragment: InventoryPart.fragments.InventoryPartBasicData,
                    fragmentName: "InventoryPartBasicData",
                  });

                  return [...existingRefs, newRef];
                },
              },
            });
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
    loading,
    error,
  };
};

export const useDeleteParts = () => {
  const [deletePart, { loading, error }] = useMutation(
    DELETE_OWN_OEM_INVENTORY_PART_MUTATION,
  );
  return {
    delete: (partId) =>
      deletePart({
        variables: {
          id: partId,
        },
        update(cache) {
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listOwnOemInventoryPart",
          });

          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listAllOwnOemMachines",
          });

          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "getOwnOemMachineById",
          });

          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listOwnOemComponents",
          });

          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "getOwnOemComponentById",
          });
          cache.gc();
        },
      }),
    loading,
    error,
  };
};

export const updateInventoryPartCache = (payload) => {
  const inventoryPart = client.readQuery({
    query: GET_INVENTORY_PART,
    variables: { id: payload._id },
  });

  if (!inventoryPart) return;

  const { getInventoryPart: cachedInventoryPart } = inventoryPart;
  if (cachedInventoryPart) {
    const cloned = { ...cachedInventoryPart };
    cloned.documentFolders = {
      ...(cloned.documentFolders ? cloned.documentFolders : {}),
      ...payload.documentFolders,
    };

    client.writeQuery({
      query: GET_INVENTORY_PART,
      variables: { id: payload._id },
      data: {
        getInventoryPart: cloned,
      },
    });
  }
};

// Stock operation mutations
export const useAddInventoryStock = () => {
  const [addStock, { loading, error }] = useMutation(
    ADD_STOCK_TO_PART_MUTATION,
  );

  return {
    add: (input) =>
      new Promise((resolve, reject) => {
        addStock({
          variables: { input },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
    loading,
    error,
  };
};

export const useRemoveInventoryStock = () => {
  const [updateStock, { loading, error }] = useMutation(
    UPDATE_STOCK_OPERATION_MUTATION,
  );

  return {
    remove: (input) =>
      new Promise((resolve, reject) => {
        updateStock({
          variables: { input },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
    loading,
    error,
  };
};

export const useDeleteInventoryStock = () => {
  const [deleteStock, { loading, error }] = useMutation(
    REMOVE_STOCK_OPERATION_MUTATION,
  );

  return {
    delete: (operationId) =>
      new Promise((resolve, reject) => {
        deleteStock({
          variables: { operationId },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
    loading,
    error,
  };
};

export const useAllocatePendingStock = () => {
  const [allocateStock, { loading, error }] = useMutation(
    ALLOCATE_PENDING_STOCK_MUTATION,
  );

  return {
    allocate: (operationId) =>
      new Promise((resolve, reject) => {
        allocateStock({
          variables: { operationId },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
    loading,
    error,
  };
};

// Backward compatibility exports
export const addPart = useAddPart;
export const updateParts = useUpdateParts;
export const deleteParts = useDeleteParts;
export const addInventoryStock = useAddInventoryStock;
export const removeInventoryStock = useRemoveInventoryStock;
export const deleteInventoryStock = useDeleteInventoryStock;
export const allocatePendingStock = useAllocatePendingStock;

export const useBulkDeleteInventoryParts = () => {
  const [bulkDelete] = useMutation(
    BULK_DELETE_OWN_OEM_INVENTORY_PARTS_MUTATION,
  );

  const bulkDeleteParts = async (ids) => {
    const { data } = await bulkDelete({
      variables: { ids },
      update(cache) {
        cache.evict({ id: "ROOT_QUERY", fieldName: "listOwnOemInventoryPart" });
        cache.evict({ id: "ROOT_QUERY", fieldName: "listAllOwnOemMachines" });
        cache.gc();
      },
    });
    return data.bulkDeleteOwnOemInventoryParts;
  };

  return { bulkDeleteParts };
};
